import { useCallback, useEffect, useState } from 'react';
import type { PaigeEmailSettings, PaigeEmailSource } from './paige.global';

export interface EmailSourceState {
  /** Undefined while loading; null when the job has no email source or the workbench is not in Paige. */
  source: PaigeEmailSource | null | undefined;
  saving: boolean;
  /** Why the last change was refused, in Paige's words. */
  error: string | null;
  save: (settings: Partial<PaigeEmailSettings>) => Promise<void>;
  release: () => Promise<void>;
}

/** The job's email source for the email window: read once, then kept as each change answers. */
export function useEmailSource(): EmailSourceState {
  const channel = window.paigeWorkbench?.email;
  const [source, setSource] = useState<PaigeEmailSource | null | undefined>(
    channel ? undefined : null,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!channel) return;
    let live = true;
    channel
      .source()
      .then((found) => live && setSource(found))
      .catch(() => live && setSource(null));
    return () => {
      live = false;
    };
  }, [channel]);
  const change = useCallback(async (answer: () => Promise<PaigeEmailSource>) => {
    setSaving(true);
    setError(null);
    try {
      setSource(await answer());
    } catch (refused) {
      setError(refused instanceof Error ? refused.message : 'The change was not saved.');
    } finally {
      setSaving(false);
    }
  }, []);
  const save = useCallback(
    async (settings: Partial<PaigeEmailSettings>) => {
      if (channel && source) await change(() => channel.save(source, settings));
    },
    [change, channel, source],
  );
  const release = useCallback(async () => {
    if (channel && source) await change(() => channel.release(source.id));
  }, [change, channel, source]);
  return { source, saving, error, save, release };
}
